'use client';

/**
 * GuestV2ProfileSheet — the "You" surface of the V2 guest menu, as Chow draws it.
 *
 * WHAT THIS REPLACES. The header's account control used to open a right-hand V1
 * sheet: a stack of place entries (`PlaceOrdersEntry`, book/reserve, "Open Chow",
 * the `CustomerAccountAffordance` panel and a link to the account page), styled
 * with the merchant template's tokens. Two taps away the same guest found the
 * Chow-styled `/account` page — two visual languages for one identity.
 *
 * This is the app's profile screen (`app/(tabs)/profile.tsx`) folded into a
 * bottom sheet:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ You                                      ✕   │
 *   ├──────────────────────────────────────────────┤
 *   │ (avatar)  Ama Mensah              [ Edit ]   │  identity, or the brand
 *   │           ama@example.com                    │  panel with a sign-in CTA
 *   │                                              │
 *   │ Account                                      │  Chow groups, hairline
 *   │   Your account                          ›    │  rows, 64px tall
 *   │   Orders and table requests             ›    │
 *   │ Preferences                                  │
 *   │   Dietary needs                         ›    │
 *   │ Support                                      │
 *   │   Send feedback                         ›    │
 *   │                                              │
 *   │ [ Log out ]                                  │
 *   │ Menuraq · Guest menu                         │
 *   └──────────────────────────────────────────────┘
 *
 * SIGN-IN IS IN THE SHEET. A signed-out guest gets the gradient card the account
 * page uses, and its button opens the Chow auth modal (`CustomerAuthModal chow`)
 * rather than routing away from the menu — the guest keeps their cart, table
 * session and scroll position.
 *
 * @location components/place/GuestV2ProfileSheet.tsx
 */

import {
  ArrowRight,
  Check,
  Globe,
  Loader2,
  LogOut,
  MessageSquare,
  ReceiptText,
  ShieldCheck,
  UserRound,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  GuestV2Button,
  GuestV2Group,
  GuestV2Row,
  GuestV2Section,
} from '@/components/guest-v2/GuestV2';
import { GuestV2AppearanceSwitch } from '@/components/guest-v2/GuestV2AppearanceSwitch';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useCustomerSession } from '@/lib/customer-bridge';
import { LANGUAGES, ORIGINAL_LANGUAGE, isOriginalLanguage } from '@/hooks/menu/useMenuTranslation';
import type { CSSProperties } from 'react';

export function GuestV2ProfileSheet({
  open,
  onOpenChange,
  placeName,
  accountHref,
  activityHref,
  onSignIn,
  onEditDietary,
  onFeedback,
  selectedLanguage,
  onSelectLanguage,
  isTranslating = false,
  paletteStyle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whose sheet this is — the branch the guest is ordering from. */
  placeName: string;
  accountHref: string;
  activityHref: string;
  onSignIn: () => void;
  onEditDietary?: () => void;
  onFeedback?: () => void;
  /**
   * The menu language control lives HERE now, not in the header: a flag
   * dropdown was taking a full 44px slot on every menu visit to serve a setting
   * most guests touch once. The sheet is where the guest already goes for
   * preferences, and the list is inline so a reader who needs it sees every
   * language in its own script rather than a panel that looks like a filter.
   */
  selectedLanguage?: string;
  onSelectLanguage?: (code: string) => void;
  isTranslating?: boolean;
  paletteStyle?: CSSProperties;
}) {
  const { data: session, signOut } = useCustomerSession();
  const [languageOpen, setLanguageOpen] = useState(false);
  const user = session?.user ?? null;
  const name = user?.name?.trim() || 'Chow guest';
  const contact = user?.phoneNumber?.trim() || user?.email?.trim() || '';
  const initial = name.charAt(0).toUpperCase();
  const language = selectedLanguage ?? ORIGINAL_LANGUAGE;
  const showingLanguage = isOriginalLanguage(language)
    ? null
    : LANGUAGES.find((entry) => entry.code === language) ?? null;
  const languageTitle = showingLanguage
    ? `${showingLanguage.flag} ${showingLanguage.native}`
    : 'Original';

  // Collapse the list whenever the sheet closes so reopening starts on the
  // summary row rather than a half-scrolled directory.
  useEffect(() => {
    if (!open) setLanguageOpen(false);
  }, [open]);

  const chooseLanguage = (code: string) => {
    onSelectLanguage?.(code);
    setLanguageOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="chow-root gv-profile-sheet" style={paletteStyle}>
        <div className="gv-profile-sheet__head">
          <SheetTitle className="gv-profile-sheet__title">You</SheetTitle>
          <button
            type="button"
            className="gv-profile-sheet__close"
            aria-label="Close profile"
            onClick={() => onOpenChange(false)}
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>
        <SheetDescription className="sr-only">
          Your account, orders and guest preferences for {placeName}.
        </SheetDescription>

        <div className="gv-profile-sheet__body">
          {user ? (
            <div className="gv-identity">
              <span className="gv-avatar" aria-hidden="true">
                {user.image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={user.image} alt="" className="size-full object-cover" />
                ) : (
                  initial
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="gv-profile-sheet__name">{name}</p>
                {contact ? <p className="gv-profile-sheet__contact">{contact}</p> : null}
              </div>
              <GuestV2Button href={accountHref} variant="secondary" size="sm">
                Edit
              </GuestV2Button>
            </div>
          ) : (
            <section className="gv-brand-panel" aria-labelledby="gv-profile-signin">
              <div className="gv-brand-panel__body">
                <p className="text-[12px] font-semibold uppercase tracking-[0.16em]">
                  {placeName}
                </p>
                <h2 id="gv-profile-signin" className="gv-h2 mt-4 text-[24px] leading-[30px]">
                  Your table, your order, your account.
                </h2>
                <p className="mt-2 text-[14px] leading-[21px]">
                  Sign in with your phone or email to keep orders and table requests together,
                  on every device.
                </p>
                <div className="mt-5">
                  <GuestV2Button variant="onbrand" block onClick={onSignIn}>
                    Sign in or create account
                    <ArrowRight aria-hidden="true" className="size-5" />
                  </GuestV2Button>
                </div>
              </div>
            </section>
          )}

          <GuestV2Section title="Account" className="mt-0">
            <GuestV2Group>
              <GuestV2Row
                href={accountHref}
                icon={UserRound}
                title="Your account"
                subtitle={user ? 'Name, phone and username' : 'Sign in to save your details'}
              />
              <GuestV2Row
                href={activityHref}
                icon={ReceiptText}
                title="Orders and table requests"
                subtitle="See your full history"
              />
            </GuestV2Group>
          </GuestV2Section>

          <GuestV2Section title="Appearance" className="mt-0">
            <GuestV2Group>
              <GuestV2AppearanceSwitch />
            </GuestV2Group>
          </GuestV2Section>

          {onEditDietary || onFeedback ? (
            <GuestV2Section title="Preferences" className="mt-0">
              <GuestV2Group>
                {onEditDietary ? (
                  <GuestV2Row
                    icon={ShieldCheck}
                    title="Dietary needs"
                    subtitle="Allergens and diets to flag on the menu"
                    onClick={onEditDietary}
                  />
                ) : null}
                {onFeedback ? (
                  <GuestV2Row
                    icon={MessageSquare}
                    title="Send feedback"
                    subtitle={`Tell ${placeName} about this order or visit`}
                    onClick={onFeedback}
                  />
                ) : null}
              </GuestV2Group>
            </GuestV2Section>
          ) : null}

          <GuestV2Section title="Language" className="mt-0">
            <GuestV2Group>
              <GuestV2Row
                icon={Globe}
                title="Menu language"
                subtitle={
                  isTranslating
                    ? 'Translating the menu…'
                    : showingLanguage
                      ? `Currently reading in ${languageTitle}`
                      : 'As the menu was written'
                }
                trailing={
                  isTranslating ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null
                }
                onClick={() => setLanguageOpen((current) => !current)}
                ariaExpanded={languageOpen}
                ariaControls="gv-language-options"
                chevron
              />
            </GuestV2Group>

            {languageOpen ? (
              <div id="gv-language-options" className="mt-2">
                <GuestV2Group>
                  <GuestV2Row
                    title="Original"
                    subtitle="As the menu was written"
                    onClick={() => chooseLanguage(ORIGINAL_LANGUAGE)}
                    trailing={
                      isOriginalLanguage(language) ? (
                        <Check aria-hidden="true" className="size-4 text-[hsl(var(--chow-accent))]" />
                      ) : null
                    }
                    chevron={false}
                  />
                  {LANGUAGES.map((entry) => (
                    <GuestV2Row
                      key={entry.code}
                      title={`${entry.flag} ${entry.native}`}
                      subtitle={entry.label}
                      onClick={() => chooseLanguage(entry.code)}
                      trailing={
                        entry.code === language ? (
                          <Check aria-hidden="true" className="size-4 text-[hsl(var(--chow-accent))]" />
                        ) : null
                      }
                      chevron={false}
                    />
                  ))}
                </GuestV2Group>
              </div>
            ) : null}
          </GuestV2Section>

          {user ? (
            <GuestV2Button
              variant="outline"
              block
              onClick={() => {
                void signOut();
                onOpenChange(false);
              }}
            >
              <LogOut aria-hidden="true" className="size-4" />
              Log out
            </GuestV2Button>
          ) : null}

          <p className="gv-profile-sheet__version">Menuraq · Guest menu</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
